// ランタイム状態（@preact/signals）。signals によりセル単位で DOM 直更新（D-0031 の狙い）。
// Slice 1 = 読取／Slice 2 = 書込（楽観反映＋サーバ権威）／Slice 3 = オフライン永続 outbox。
import { signal, computed } from "@preact/signals";
import { getPublishedApp, getDraftApp, readRows, writeRows } from "../api/client";
import type { Mutation } from "../api/client";
import {
  upsertDraft,
  draftToMutation,
  createToMutation,
  deleteToMutation,
  reconcile,
  reconcileDelete,
  rowValues,
  newIdempotencyKey,
} from "../sync/outbox.mjs";
import { outboxKey, serializeOutbox, deserializeOutbox, countPending } from "../sync/persist.mjs";
// @ts-ignore — reference の KV 実装を流用（SSOT を複製しない）。Vite がバンドルする。
import { createIdbKV } from "../../../reference/core/idb-kv.mjs";
import type { AppDefinition, View, Row } from "../types";

/** 1 行分の保留編集（outbox.mjs のドラフト）。 */
type Draft = { values: Record<string, unknown>; base: Record<string, unknown>; baseVersion: number };
/** 同期状態。offline=送信保留（オフライン）／pending=送信待ち（オンライン復帰待ち等）。 */
export type SyncState = {
  status: "idle" | "saving" | "saved" | "conflict" | "error" | "offline";
  conflicts?: string[];
  error?: string;
  pending?: number;
};

export const appSig = signal<AppDefinition | null>(null);
export const activeViewIdSig = signal<string | null>(null);
export const statusSig = signal<"idle" | "loading" | "ready" | "error">("idle");
export const errorSig = signal<string | null>(null);
/** tableId → 行配列（読み込み済みのみ）。 */
export const rowsByTableSig = signal<Record<string, Row[]>>({});

export const activeViewSig = computed<View | null>(() => {
  const app = appSig.value;
  const id = activeViewIdSig.value;
  if (!app || !id) return null;
  return app.views.find((v) => v.id === id) ?? null;
});

/** アクティブビューが参照するテーブルの行（未読なら空配列）。 */
export const activeRowsSig = computed<Row[]>(() => {
  const app = appSig.value;
  const view = activeViewSig.value;
  if (!app || !view) return [];
  return rowsByTableSig.value[view.tableId] ?? [];
});

// ============================================================================
// 行選択とビュー間ナビゲーション（一覧 → 詳細/フォーム → 戻る）
// ============================================================================
/** 選択中の行 ID（detail/form が対象行を特定するのに使う）。 */
export const selectedRowIdSig = signal<string | null>(null);
/** 「戻る」用のビュー履歴（行選択で遷移したときだけ積む）。 */
export const viewStackSig = signal<string[]>([]);

/** 選択中の行（アクティブビューのテーブルから解決。未選択/不在なら null）。 */
export const selectedRowSig = computed<Row | null>(() => {
  const id = selectedRowIdSig.value;
  if (!id) return null;
  return activeRowsSig.value.find((r) => r.__rowId === id) ?? null;
});

/** tableId の詳細表示先ビュー（detail 優先・なければ form）。 */
export function detailViewFor(app: AppDefinition, tableId: string): View | null {
  return (
    app.views.find((v) => v.type === "detail" && v.tableId === tableId) ??
    app.views.find((v) => v.type === "form" && v.tableId === tableId) ??
    null
  );
}

/** tableId の入力フォームビュー。 */
export function formViewFor(app: AppDefinition, tableId: string): View | null {
  return app.views.find((v) => v.type === "form" && v.tableId === tableId) ?? null;
}

/** 一覧などで行をタップ → その行の詳細/フォームへ（対象ビューが無ければ何もしない）。 */
export function openRow(row: Row): void {
  const app = appSig.value;
  const view = activeViewSig.value;
  if (!app || !view || !row.__rowId) return;
  const target = detailViewFor(app, view.tableId);
  if (!target || target.id === view.id) return;
  viewStackSig.value = [...viewStackSig.value, view.id];
  selectedRowIdSig.value = row.__rowId;
  void setActiveView(target.id);
}

/** 行選択遷移からの「戻る」。未編集の新規行はこのタイミングで破棄する（空行を作らない）。 */
export function goBack(): void {
  discardUntouchedCreate();
  const stack = viewStackSig.value;
  const prev = stack[stack.length - 1];
  viewStackSig.value = stack.slice(0, -1);
  selectedRowIdSig.value = null;
  if (prev) void setActiveView(prev);
}

/** 行コンテキスト付きの navigate アクション（カード上の「結果を入力」等）。
 *  行を選択してから遷移する＝遷移先の detail/form がその行を対象にする。 */
export function openRowIn(row: Row, viewId: string): void {
  const view = activeViewSig.value;
  if (!row.__rowId) {
    void setActiveView(viewId);
    return;
  }
  if (view && view.id !== viewId) viewStackSig.value = [...viewStackSig.value, view.id];
  selectedRowIdSig.value = row.__rowId;
  void setActiveView(viewId);
}

/** ナビバーからの直接遷移（履歴と選択をリセット）。 */
export function navigateTo(viewId: string): void {
  discardUntouchedCreate();
  viewStackSig.value = [];
  selectedRowIdSig.value = null;
  void setActiveView(viewId);
}

function tableRefOf(app: AppDefinition, tableId: string): string | null {
  const t = app.tables.find((x) => x.id === tableId);
  return t?.sourceRef ?? null;
}

async function ensureRowsFor(app: AppDefinition, tableId: string): Promise<void> {
  if (rowsByTableSig.value[tableId]) return; // 既読
  const tableRef = tableRefOf(app, tableId);
  if (!tableRef) return;
  const rows = await readRows(app.id, tableRef);
  rowsByTableSig.value = { ...rowsByTableSig.value, [tableId]: rows };
}

/** アプリを読み込み、ホームビューを表示する。draft=true で下書きプレビュー。 */
export async function loadApp(appId: string, opts: { draft?: boolean } = {}): Promise<void> {
  statusSig.value = "loading";
  errorSig.value = null;
  try {
    const app = opts.draft ? await getDraftApp(appId) : await getPublishedApp(appId);
    appSig.value = app;
    const home = app.settings?.homeViewId ?? app.navigation?.[0]?.viewId ?? app.views?.[0]?.id ?? null;
    activeViewIdSig.value = home;
    const view = home ? app.views.find((v) => v.id === home) : null;
    if (view) await ensureRowsFor(app, view.tableId);
    statusSig.value = "ready";
    await hydrateOutbox(app); // Slice 3: 永続化した保留編集を復元し、オンラインなら再送
  } catch (e) {
    errorSig.value = (e as Error).message;
    statusSig.value = "error";
  }
}

/** ナビ切替。対象ビューのテーブル行を必要に応じて読み込む。 */
export async function setActiveView(viewId: string): Promise<void> {
  const app = appSig.value;
  if (!app) return;
  activeViewIdSig.value = viewId;
  const view = app.views.find((v) => v.id === viewId);
  if (view) {
    try {
      await ensureRowsFor(app, view.tableId);
    } catch (e) {
      errorSig.value = (e as Error).message;
    }
  }
}

// ============================================================================
// 書込経路（Slice 2/3）: 楽観反映 → デバウンス flush → サーバ権威で反映
//   Slice 3 追加: オフライン耐性（送信保留＋再接続 flush）＋ IndexedDB 永続 outbox。
// ============================================================================
export const syncSig = signal<SyncState>({ status: "idle" });
/** オンライン状態（online/offline イベントで更新）。SSR/node では true 既定。 */
export const isOnlineSig = signal<boolean>(typeof navigator !== "undefined" ? navigator.onLine : true);
/** 保留編集（作成/更新）: tableId → (rowId → Draft)。 */
const draftsSig = signal<Record<string, Record<string, Draft>>>({});
/** 保留削除: tableId → rowId[]。 */
const deletesSig = signal<Record<string, string[]>>({});
/** サーバ未作成の新規行（create が成功するまで）。flush は update ではなく create を送る。 */
const pendingCreates = new Set<string>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
const FLUSH_DELAY = 400;

/** 保留件数（バッジ表示）。作成/更新の行数＋削除の行数。 */
export const pendingSig = computed<number>(() => countPending({ drafts: draftsSig.value, deletes: deletesSig.value }));
function pendingCountNow(): number {
  return countPending({ drafts: draftsSig.value, deletes: deletesSig.value });
}
function hasPendingNow(): boolean {
  return pendingCountNow() > 0;
}

// --- IndexedDB 永続化（reference/core/idb-kv.mjs を流用） -----------------------
let _kv: { get(k: string): Promise<unknown>; set(k: string, v: unknown): Promise<void> } | null = null;
let _kvInit = false;
function getKV() {
  if (_kvInit) return _kv;
  _kvInit = true;
  try {
    if (typeof indexedDB !== "undefined") _kv = createIdbKV("sheetsnap", "outbox");
  } catch {
    _kv = null; // 非対応環境（プライベートモード等）では永続化を諦める＝メモリのみ
  }
  return _kv;
}

/** 現在の保留キューを IndexedDB に保存する（fire-and-forget。失敗は致命的でない）。 */
async function persist(): Promise<void> {
  const kv = getKV();
  const app = appSig.value;
  if (!kv || !app) return;
  try {
    await kv.set(outboxKey(app.id), serializeOutbox({ appId: app.id, drafts: draftsSig.value, pendingCreates, deletes: deletesSig.value }));
  } catch {
    /* 永続化失敗は無視（同期自体はメモリ上で継続） */
  }
}

// --- 行のローカル操作 ---------------------------------------------------------
function findRow(tableId: string, rowId: string): Row | undefined {
  return (rowsByTableSig.value[tableId] ?? []).find((r) => r.__rowId === rowId);
}

/** rowsByTable 内の 1 行を部分更新（__rowId で特定）。 */
function patchLocalRow(tableId: string, rowId: string, patch: Row): void {
  const cur = rowsByTableSig.value[tableId];
  if (!cur) return;
  rowsByTableSig.value = {
    ...rowsByTableSig.value,
    [tableId]: cur.map((r) => (r.__rowId === rowId ? { ...r, ...patch } : r)),
  };
}

/** rowsByTable から 1 行を除去（楽観削除・新規行の破棄）。 */
function removeLocalRow(tableId: string, rowId: string): void {
  const cur = rowsByTableSig.value[tableId];
  if (!cur) return;
  rowsByTableSig.value = { ...rowsByTableSig.value, [tableId]: cur.filter((r) => r.__rowId !== rowId) };
}

/** 保留ドラフトから 1 行を除去。 */
function removeDraft(tableId: string, rowId: string): void {
  const perTable = { ...(draftsSig.value[tableId] ?? {}) };
  if (rowId in perTable) {
    delete perTable[rowId];
    draftsSig.value = { ...draftsSig.value, [tableId]: perTable };
  }
}

/** テーブルの主キー列の保存キー（列の sourceRef 優先）。無ければ null。 */
function primaryKeyOf(app: AppDefinition, tableId: string): string | null {
  const table = app.tables.find((t) => t.id === tableId);
  const pk = table?.columns.find((c) => c.id === table.primaryKey);
  return pk ? ((pk as { sourceRef?: string }).sourceRef ?? pk.id) : null;
}

/** 未編集のまま放置された新規行を破棄する（空行をサーバに作らない）。
 *  主キーは createRow が自動採番するため「主キーだけの行」も未編集とみなす。 */
function discardUntouchedCreate(): void {
  const id = selectedRowIdSig.value;
  const view = activeViewSig.value;
  const app = appSig.value;
  if (!id || !view || !app || !pendingCreates.has(id)) return;
  const tableId = view.tableId;
  const stored = findRow(tableId, id);
  const hasDraft = Object.keys(draftsSig.value[tableId]?.[id]?.values ?? {}).length > 0;
  if (hasDraft) return;
  const pkKey = primaryKeyOf(app, tableId);
  const keys = Object.keys(rowValues(stored ?? {}) as Record<string, unknown>).filter((k) => k !== pkKey);
  if (keys.length > 0) return;
  pendingCreates.delete(id);
  removeLocalRow(tableId, id);
  void persist();
}

/** アクティブビューのテーブルに新規行を追加し、フォームへ遷移する（form ビューが無ければ何もしない）。
 *  主キー列に ID を自動採番する（Sheets コネクタの create はキー列の値が必須／native でも一貫）。 */
export function createRow(): void {
  const app = appSig.value;
  const view = activeViewSig.value;
  if (!app || !view) return;
  const tableId = view.tableId;
  const form = formViewFor(app, tableId);
  if (!form) return;
  const rowId = `r-${(newIdempotencyKey(tableId) as string).replace(/-/g, "").slice(0, 12)}`;
  const newRow: Row = { __rowId: rowId, __version: 0 };
  const pkKey = primaryKeyOf(app, tableId);
  if (pkKey) newRow[pkKey] = rowId; // Sheets の rowId = キー列の値（安定 rowId 契約）
  const cur = rowsByTableSig.value[tableId] ?? [];
  rowsByTableSig.value = { ...rowsByTableSig.value, [tableId]: [...cur, newRow] };
  pendingCreates.add(rowId);
  // 永続化は最初の編集(editCell)で行う＝未編集の新規行はキューに残さない（hydrate時の孤児回避）。
  viewStackSig.value = [...viewStackSig.value, view.id];
  selectedRowIdSig.value = rowId;
  void setActiveView(form.id);
}

/** 行を削除する。楽観的に除去 → 削除キューへ積み flush（オフラインなら保留・再接続で送信）。 */
export async function deleteRow(row: Row): Promise<void> {
  const app = appSig.value;
  const view = activeViewSig.value;
  const rowId = row.__rowId;
  if (!app || !view || !rowId) return;
  const tableId = view.tableId;

  removeDraft(tableId, rowId); // 保留編集は削除で無効化
  removeLocalRow(tableId, rowId);
  if (selectedRowIdSig.value === rowId) selectedRowIdSig.value = null;

  // サーバ未作成の新規行はローカル破棄のみ（サーバへ delete を送らない）
  if (pendingCreates.has(rowId)) {
    pendingCreates.delete(rowId);
    void persist();
    return;
  }

  const dcur = deletesSig.value[tableId] ?? [];
  if (!dcur.includes(rowId)) deletesSig.value = { ...deletesSig.value, [tableId]: [...dcur, rowId] };
  void persist();
  await flush();
}

/** セル/フィールド編集。楽観反映＋ドラフト畳み込み＋永続化＋デバウンス flush。 */
export function editCell(row: Row, key: string, value: unknown): void {
  const app = appSig.value;
  const view = activeViewSig.value;
  if (!app || !view) return;
  const tableId = view.tableId;
  const rowId = row.__rowId;
  if (!rowId) return; // __rowId の無い行は書き戻せない（新規行は createRow が ID を発行済み）

  // base は「楽観反映前の値」を初回だけ確定する
  const stored = findRow(tableId, rowId) ?? row;
  const orig = stored[key];
  const perTable = draftsSig.value[tableId] ?? {};
  const nextDraft = upsertDraft(perTable[rowId], key, value, orig, (stored.__version as number) ?? 0) as Draft;
  draftsSig.value = { ...draftsSig.value, [tableId]: { ...perTable, [rowId]: nextDraft } };

  patchLocalRow(tableId, rowId, { [key]: value }); // 楽観反映
  void persist(); // ブラウザを閉じても編集が残る（Slice 3）

  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => void flush(), FLUSH_DELAY);
}

/** 送信用にテーブルのキューを抜き取り、signal からクリアする（送信中の再編集は新規エントリとして溜まる）。 */
function takeTableQueue(tableId: string): { drafts: Record<string, Draft>; deletes: string[] } {
  const drafts = draftsSig.value[tableId] ?? {};
  const deletes = deletesSig.value[tableId] ?? [];
  const nd = { ...draftsSig.value }; delete nd[tableId]; draftsSig.value = nd;
  const nx = { ...deletesSig.value }; delete nx[tableId]; deletesSig.value = nx;
  return { drafts, deletes };
}

/** ネットワーク失敗時にキューを戻す（送信中の再編集=current を優先マージ）。 */
function restoreTableQueue(tableId: string, taken: { drafts: Record<string, Draft>; deletes: string[] }): void {
  const curD = draftsSig.value[tableId] ?? {};
  draftsSig.value = { ...draftsSig.value, [tableId]: { ...taken.drafts, ...curD } };
  const curX = deletesSig.value[tableId] ?? [];
  deletesSig.value = { ...deletesSig.value, [tableId]: [...new Set([...taken.deletes, ...curX])] };
}

/**
 * 保留編集（作成/更新＋削除）をサーバへ送信し、結果を反映する。
 * - オフラインなら送信せずキュー保持（永続化済み）→ 再接続時に自動再送。
 * - サーバから応答が返った分はキューから除去（成功=反映／競合=サーバ値で置換／app-error=破棄）。
 * - ネットワーク失敗（到達不能）はキューを戻して次回リトライ＝**編集を失わない**（Slice 2 のバグ修正）。
 */
export async function flush(): Promise<void> {
  if (flushTimer) { clearTimeout(flushTimer); flushTimer = null; }
  const app = appSig.value;
  if (!app) return;
  if (!hasPendingNow()) {
    if (syncSig.value.status === "saving" || syncSig.value.status === "offline") syncSig.value = { status: "idle" };
    return;
  }
  // オフライン: 送信せず保留（永続化済み）。online イベントで再送される。
  if (!isOnlineSig.value) {
    syncSig.value = { status: "offline", pending: pendingCountNow() };
    return;
  }

  syncSig.value = { status: "saving" };
  let anyConflict: string[] | null = null;
  let anyError: string | null = null;
  let networkFailed = false;

  const tableIds = new Set<string>([
    ...Object.keys(draftsSig.value).filter((t) => Object.keys(draftsSig.value[t]).length > 0),
    ...Object.keys(deletesSig.value).filter((t) => (deletesSig.value[t] ?? []).length > 0),
  ]);

  for (const tableId of tableIds) {
    const tableRef = tableRefOf(app, tableId);
    if (!tableRef) continue;
    const taken = takeTableQueue(tableId);

    const upsertIds = Object.keys(taken.drafts);
    // サーバ未作成の行は update ではなく create（行の全値を載せる。以後の編集は update）
    const wasCreate = upsertIds.map((id) => pendingCreates.has(id));
    const upsertMuts = upsertIds.map((rowId, i) => {
      if (wasCreate[i]) {
        const stored = findRow(tableId, rowId) ?? { __rowId: rowId };
        return createToMutation(rowId, stored, newIdempotencyKey(rowId));
      }
      return draftToMutation(rowId, taken.drafts[rowId], newIdempotencyKey(rowId));
    }) as Mutation[];
    const delMuts = taken.deletes.map((rowId) => deleteToMutation(rowId, newIdempotencyKey(rowId))) as Mutation[];
    const muts = [...upsertMuts, ...delMuts];
    if (muts.length === 0) continue;

    try {
      const results = await writeRows(app.id, tableRef, muts);
      results.slice(0, upsertIds.length).forEach((res, i) => {
        const rowId = upsertIds[i];
        const stored = findRow(tableId, rowId) ?? { __rowId: rowId };
        const rec = reconcile(stored, res) as { row: Row; status: string; conflicts?: string[]; error?: string };
        patchLocalRow(tableId, rowId, rec.row);
        if (rec.status === "conflict") anyConflict = rec.conflicts ?? [];
        else if (rec.status === "error") anyError = rec.error ?? "error";
        else if (wasCreate[i]) pendingCreates.delete(rowId); // create 成功 → 以後は update
      });
      results.slice(upsertIds.length).forEach((res) => {
        const rec = reconcileDelete(res) as { status: string; error?: string };
        if (rec.status === "error") anyError = rec.error ?? "error";
      });
    } catch (e) {
      restoreTableQueue(tableId, taken); // 到達不能 → 戻して次回リトライ（編集を失わない）
      anyError = (e as Error).message;
      networkFailed = true;
    }
  }

  void persist();

  syncSig.value = anyConflict
    ? { status: "conflict", conflicts: anyConflict }
    : networkFailed
    ? { status: "offline", pending: pendingCountNow() } // 到達不能はオフライン扱い（保留を明示）
    : anyError
    ? { status: "error", error: anyError }
    : { status: "saved" };
}

// --- 永続化の復元＋接続監視（Slice 3） --------------------------------------
/** 永続化した保留キューを復元し、ローカルビューへ反映する（loadApp から呼ぶ）。 */
async function hydrateOutbox(app: AppDefinition): Promise<void> {
  const kv = getKV();
  if (!kv) return;
  let snap: unknown = null;
  try { snap = await kv.get(outboxKey(app.id)); } catch { return; }
  if (!snap) return;

  const { drafts, pendingCreates: pc, deletes } = deserializeOutbox(snap);
  draftsSig.value = drafts;
  deletesSig.value = deletes;
  pendingCreates.clear();
  (pc as Set<string>).forEach((id) => pendingCreates.add(id));

  const tableIds = new Set<string>([...Object.keys(drafts), ...Object.keys(deletes)]);
  for (const tableId of tableIds) {
    try { await ensureRowsFor(app, tableId); } catch { /* オフライン等: サーバ行は空でも保留は反映する */ }
    for (const rowId of deletes[tableId] ?? []) removeLocalRow(tableId, rowId); // 削除の楽観反映
    const perTable = drafts[tableId] ?? {};
    for (const rowId of Object.keys(perTable)) {
      const values = (perTable[rowId].values ?? {}) as Row;
      if (pendingCreates.has(rowId) && !findRow(tableId, rowId)) {
        // サーバに無い新規行 → ドラフト値から復元して挿入
        const cur = rowsByTableSig.value[tableId] ?? [];
        rowsByTableSig.value = { ...rowsByTableSig.value, [tableId]: [...cur, { __rowId: rowId, __version: 0, ...values }] };
      } else {
        patchLocalRow(tableId, rowId, values); // 既存行に保留編集を重ねる
      }
    }
  }

  if (!hasPendingNow()) return;
  if (isOnlineSig.value) void flush();
  else syncSig.value = { status: "offline", pending: pendingCountNow() };
}

/** online/offline を監視し、復帰時に自動 flush する（一度だけ束ねる。main から呼ぶ）。 */
let connectivityBound = false;
export function bindConnectivity(): void {
  if (connectivityBound || typeof window === "undefined") return;
  connectivityBound = true;
  window.addEventListener("online", () => {
    isOnlineSig.value = true;
    void flush();
  });
  window.addEventListener("offline", () => {
    isOnlineSig.value = false;
    if (hasPendingNow()) syncSig.value = { status: "offline", pending: pendingCountNow() };
  });
}
